import { HostListener, Injectable } from '@angular/core';
import { CalculationBaseFormFactory } from './calculation-base-form.factory';
import { Subscription } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import {
  CalculationResponse,
} from '../../../../../../../../../../../common/dist/angular/responses/calculation.response';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import {
  CalculationStatusEnum,
} from '../../../../../../../../../../../common/src/enums/calculations/calculation-status.enum';
import { TranslateService } from '@ngx-translate/core';
import { CalculationStore } from '../../../../../stores/calculation.store';
import {OptionsMapper} from "@magieno/angular-advanced-forms";
import {
  WarningCalculationInProgressComponent,
} from '../../../../warning-calculation-in-progress/warning-calculation-in-progress.component';
import {
  WarningResettingToDraftComponent,
} from '../../../../warning-resetting-to-draft/warning-resetting-to-draft.component';
import {
  WarningResultsResettingComponent,
} from '../../../../warning-results-resetting/warning-results-resetting.component';
import { AdvancedFormButton } from "@magieno/angular-advanced-forms";
import { AdvancedForm } from "@magieno/angular-advanced-forms";
import {
  CalculationRunOptions,
} from '../../../../../../../../../../../common/src/options/calculations/calculation-run.options';
import { CalculationsUtils } from '../../../../../utils/calculations.utils';
import { AlertMessageFactory } from '../../../../../../core/factories/alert-message.factory';
import { SeverityTypeEnum } from '../../../../../../core/enums/severity-type.enum';
import { AlertMessageStore } from '../../../../../../core/stores/alert-message.store';
import { FormLifecycleStatusEnum } from '@magieno/angular-advanced-forms';
import { ValidationError } from '@pristine-ts/class-validator';
import { ImportManager } from '../../../../../managers/import.manager';
import {
  CalculationKeynameEnum,
} from '../../../../../../../../../../../common/src/enums/calculations/calculation-keyname.enum';
import { FormChangeManager } from '../../../../../managers/form-change.manager';
import { CalculationLifecycleManager } from '../../../../../managers/calculation-lifecycle.manager';
import { EventManager } from '../../../../../../core/managers/event.manager';
import { CalculationEventEnum } from '../../../../../../core/enums/calculation-event.enum';

@Injectable()
export abstract class CalculationBaseForm<T extends CalculationRunOptions> {
  calculationId: string;

  calculationResponseSubscription: Subscription;
  calculationErrorsSubscription: Subscription;

  formServiceOnButtonClickSubscription: Subscription;
  onFormChangeSubscription: Subscription;
  revalidateSubscription: Subscription;

  calculationResponse: CalculationResponse;

  public lastModified: Date;

  public calculationStatusEnum: CalculationStatusEnum;

  // @ts-ignore
  public form: AdvancedForm<T> = this.formFactory.create();
  public numberOfErrors = 0;
  protected optionsType: { new(...args: any[]): object; };
  private importManagerSubscription?: Subscription;

  constructor(public readonly formFactory: CalculationBaseFormFactory,
              private readonly calculationStore: CalculationStore,
              protected readonly translateService: TranslateService,
              private readonly activatedRoute: ActivatedRoute,
              public modalService: NgbModal,
              private readonly router: Router,
              private readonly optionsMapper: OptionsMapper,
              private readonly calculationsUtils: CalculationsUtils,
              private readonly alertMessageStore: AlertMessageStore,
              private readonly calculationKeyname: CalculationKeynameEnum,
              private readonly importManager: ImportManager,
              private readonly calculationLifecycleManager: CalculationLifecycleManager,
              private readonly formChangeManager: FormChangeManager,
              protected readonly eventManager: EventManager,
              private readonly validationGroups: string[] = [],
  ) {

    // Start by setting the calculationId
    this.calculationId = this.activatedRoute.snapshot.params.calculationId;
  }

  ngOnInit(): void {
    // Right away, we want to form to be set as loading.
    this.form.lifecycleStatus = FormLifecycleStatusEnum.Loading;

    this.activatedRoute.params.subscribe(params => {
      if (params.calculationId === undefined) {
        return;
      }

      this.calculationId = params.calculationId;

      // Unsubscribe all the subscriptions on route change.
      this.unsubscribeAll();

      this.form.lifecycleStatus = FormLifecycleStatusEnum.Loading;

      this.calculationResponseSubscription = this.calculationStore.get(params.calculationId).subscribe(async (calculationResponse) => {
        if (calculationResponse === undefined) {
          return;
        }

        if (calculationResponse.options?.options) {

          // It's important to init the form since when parameters change, we need to reload it entirely.
          // todo: I hope that what I just changed is the expected behavior.
          const options: T = this.optionsMapper.map<T>(new this.optionsType() as T, calculationResponse.options.options);
          this.form.setValue(options);

          // This following method registers an observable on any button clicked in the form. Each button has an id
          // and that's how we know which button was clicked on.
          if (this.formServiceOnButtonClickSubscription) {
            this.formServiceOnButtonClickSubscription.unsubscribe();
          }
          this.formServiceOnButtonClickSubscription = this.form.buttonClick$.subscribe(async (button: AdvancedFormButton) => {
            switch (button.id) {
              case 'SAVE':
                await this.trySave();
                break;
              case 'EXECUTE':
                await this.tryRun();
                break;
            }
          });

          if (this.onFormChangeSubscription) {
            this.onFormChangeSubscription.unsubscribe();
          }
          this.onFormChangeSubscription = this.form.value$.subscribe((value) => {
            // this.refresh();

            this.formChangeManager.formHasChanged(this.form);
          });
          // this.formService.form.updateModel(calculationResponse.options.options);
        }

        this.calculationResponse = calculationResponse;

        this.calculationStatusEnum = calculationResponse.status;

        this.lastModified = this.calculationsUtils.getLastModifiedDate(calculationResponse);

        this.form.lifecycleStatus = FormLifecycleStatusEnum.ReadyForInput;

        this.postUpdateCallback();

        // await this.form.validate();
        this.refresh();

        if (this.calculationStatusEnum === CalculationStatusEnum.Completed || this.calculationStatusEnum === CalculationStatusEnum.Error) {
          this.calculationLifecycleManager.calculationCompleted.next(this.calculationResponse);
        }
      });

      this.calculationErrorsSubscription = this.calculationStore.getErrors(params.calculationId).subscribe(errors => {
        this.numberOfErrors = errors.filter(error => error.messages.length > 0).length;
      });

      this.importManagerSubscription = this.importManager.importedModel.subscribe(options => {
        if (options.calculationKeyname === this.calculationKeyname) {
          this.form.setValue(options as T);
        }
      });

      // When an item is removed from a form array (e.g. a child), the previously computed
      // errors still reference the old indexes. Re-validate so the error list reflects the
      // current structure and no longer points at elements that no longer exist.
      this.revalidateSubscription = this.eventManager.eventStream.subscribe(async (event) => {
        if (event === CalculationEventEnum.FormArrayItemDeleted) {
          await this.revalidate();
        }
      });
    });
  }

  /**
   * Re-runs validation and refreshes the error list. Used after structural changes to the
   * form (e.g. deleting an array item) so the errors panel no longer references elements
   * that have been removed.
   */
  async revalidate() {
    if (this.calculationResponse === undefined) {
      return;
    }

    const validationErrors = await this.form.validate({ validationGroups: this.validationGroups });
    this.setErrors(validationErrors);
  }

  /**
   * This method exists so that forms extending the base form can be notified when the calculation has
   * been updated and proceed to update some fields.
   */
  postUpdateCallback() {

  }

  ngOnDestroy() {
    this.unsubscribeAll();
  }

  setErrors(validationErrors: ValidationError[]) {
    const errors = this.calculationsUtils.getErrors(validationErrors);
    this.calculationStore.setErrors(this.calculationId, errors);
  }

  async trySave() {
    // Todo: Handle errors
    if (this.calculationResponse.status === CalculationStatusEnum.Created) {
      await this.save();
      return;
    }
    if (this.calculationResponse.status === CalculationStatusEnum.InProgress) {
      const modal = this.modalService.open(WarningCalculationInProgressComponent);
      modal.closed.subscribe(async result => {
        return;
      });
    }

    const modal = this.modalService.open(WarningResettingToDraftComponent);
    modal.closed.subscribe(async result => {
      if (result === true) {
        await this.save();
        return;
      }
    });
  }

  async save() {
    try {
      // We want the form to be disabled  automatically so we set everything to isSending so we can't modify anything.
      this.form.lifecycleStatus = FormLifecycleStatusEnum.Sending;

      const validationErrors = await this.form.validate({ validationGroups: this.validationGroups });
      this.setErrors(validationErrors);

      await this.calculationStore.update(this.calculationId, this.form.value as CalculationRunOptions);
    } catch (error) {
      this.form.lifecycleStatus = FormLifecycleStatusEnum.ReadyForInput;
    }
  }

  async tryRun() {
    if (this.calculationResponse.resultId === undefined) {
      await this.run();
      return;
    }
    if (this.calculationResponse.status === CalculationStatusEnum.InProgress) {
      const modal = this.modalService.open(WarningCalculationInProgressComponent);
      modal.closed.subscribe(async result => {
        return;
      });
    }

    const modal = this.modalService.open(WarningResultsResettingComponent);
    modal.closed.subscribe(async result => {
      if (result === true) {
        await this.run();
        return;
      }
    });
  }

  async run() {
    this.form.lifecycleStatus = FormLifecycleStatusEnum.Sending;

    const validationErrors = await this.form.validate({ validationGroups: this.validationGroups });
    this.setErrors(validationErrors);
    if (validationErrors && validationErrors.length > 0) {
      this.form.setButtonLifecycleStatus(FormLifecycleStatusEnum.ReadyForInput);
      this.form.lifecycleStatus = FormLifecycleStatusEnum.ReadyForInput;
      return;
    }

    try {
      this.calculationLifecycleManager.calculationStartedRunning.next(this.form.value);
      await this.calculationStore.run(this.calculationId, this.form.value);
      await this.router.navigate(['results'], { relativeTo: this.activatedRoute.parent });
    } catch (e) {
      if (e.status === 400 && e.error?.message === 'Validation error') {
        this.alertMessageStore.saveAll(AlertMessageFactory.createValidationAlertMessage(e, SeverityTypeEnum.Error));
      }

      this.calculationLifecycleManager.calculationCompleted.next(this.calculationResponse);
    }
    // We reset the form to it's initial state after a successful execution trigger.
    this.form.setButtonLifecycleStatus(FormLifecycleStatusEnum.ReadyForInput);
    this.form.lifecycleStatus = FormLifecycleStatusEnum.ReadyForInput;
  }

  refresh() {
  }

  getStatusText(): string {
    return this.calculationsUtils.getStatusText(this.calculationStatusEnum);
  }

  getLastModifiedText() {
    return this.calculationsUtils.getLastModifiedText(this.lastModified);
  }

  @HostListener('window:beforeunload', ['$event'])
  beforeUnloadHandler(event) {
    if (this.form.pristine === true ||
      this.calculationStatusEnum === CalculationStatusEnum.Completed ||
      this.calculationStatusEnum === CalculationStatusEnum.Error ||
      this.calculationStatusEnum === CalculationStatusEnum.InProgress) {
      return true;
    }
    return false;
  }

  public openErrorsSidebar() {
    this.eventManager.publishEvent(CalculationEventEnum.ErrorsBadgeClicked);
  }

  private unsubscribeAll() {
    this.calculationResponseSubscription?.unsubscribe();
    this.formServiceOnButtonClickSubscription?.unsubscribe();
    this.onFormChangeSubscription?.unsubscribe();
    this.calculationErrorsSubscription?.unsubscribe();
    this.importManagerSubscription?.unsubscribe();
    this.revalidateSubscription?.unsubscribe();
  }
}
